import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Plus, Tag, Edit, Trash2, Users, Calendar, Percent, Clock, Link, Copy, Check } from "lucide-react";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { getDisplayName } from "@/lib/userDisplay";

interface DiscountCode {
  id: string;
  code: string;
  type: string;
  value: number;
  applicablePlans: string[] | null;
  maxUses: number | null;
  timesUsed: number;
  stripeCouponId: string | null;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
  linkSlug: string | null;
}

interface DiscountCodeUsage {
  id: string;
  discountCodeId: string;
  userId: string;
  subscriptionId: string | null;
  discountAmount: number | null;
  usedAt: string;
  user: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
  };
}

function CopyableLink({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `https://edufocalacademy.com/trial/${slug}`;

  const copy = () => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-2 mt-2 p-2 bg-muted/50 rounded-md border">
      <Link className="w-3 h-3 text-muted-foreground shrink-0" />
      <span className="text-xs font-mono text-primary truncate flex-1">{url}</span>
      <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={copy}>
        {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      </Button>
    </div>
  );
}

export default function DiscountCodeManager() {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);
  const [viewingUsage, setViewingUsage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    code: "",
    type: "percentage",
    value: 10,
    applicablePlans: [] as string[],
    maxUses: null as number | null,
    expiresAt: null as string | null,
    active: true,
    linkSlug: "",
  });

  const { data: codes = [], isLoading } = useQuery<DiscountCode[]>({
    queryKey: ["/api/admin/discount-codes"],
  });

  const { data: usageData = [] } = useQuery<DiscountCodeUsage[]>({
    queryKey: ["/api/admin/discount-codes", viewingUsage, "usage"],
    enabled: !!viewingUsage,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/admin/discount-codes", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/discount-codes"] });
      toast({ title: "Success", description: "Discount code created successfully" });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create discount code", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PUT", `/api/admin/discount-codes/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/discount-codes"] });
      toast({ title: "Success", description: "Discount code updated successfully" });
      setEditingCode(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update discount code", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/admin/discount-codes/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/discount-codes"] });
      toast({ title: "Success", description: "Discount code deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete discount code", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      code: "",
      type: "percentage",
      value: 10,
      applicablePlans: [],
      maxUses: null,
      expiresAt: null,
      active: true,
      linkSlug: "",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const slugValue = formData.linkSlug.trim()
      ? formData.linkSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-")
      : null;

    const payload = {
      ...formData,
      linkSlug: slugValue,
      applicablePlans: formData.applicablePlans.length > 0 ? formData.applicablePlans : null,
    };

    if (editingCode) {
      updateMutation.mutate({ id: editingCode.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (code: DiscountCode) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      type: code.type,
      value: code.value,
      applicablePlans: code.applicablePlans || [],
      maxUses: code.maxUses,
      expiresAt: code.expiresAt ? code.expiresAt.split('T')[0] : null,
      active: code.active,
      linkSlug: code.linkSlug || "",
    });
  };

  const handlePlanToggle = (plan: string) => {
    setFormData(prev => ({
      ...prev,
      applicablePlans: prev.applicablePlans.includes(plan)
        ? prev.applicablePlans.filter(p => p !== plan)
        : [...prev.applicablePlans, plan]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Discount Codes</h2>
          <p className="text-muted-foreground">Manage subscription discount codes, free trials, and trial links</p>
        </div>
        <Dialog open={isCreateOpen || !!editingCode} onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingCode(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsCreateOpen(true)} data-testid="button-create-discount">
              <Plus className="w-4 h-4 mr-2" />
              Create Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingCode ? "Edit" : "Create"} Discount Code</DialogTitle>
              <DialogDescription>
                {formData.type === 'percentage' 
                  ? "Create a percentage discount for subscriptions" 
                  : "Create a free trial code for new subscribers"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SAVE20"
                    required
                    data-testid="input-code"
                  />
                  <p className="text-xs text-muted-foreground">Code will be converted to uppercase</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value, linkSlug: value === "percentage" ? "" : prev.linkSlug }))}>
                    <SelectTrigger id="type" data-testid="select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage Discount</SelectItem>
                      <SelectItem value="trial">Free Trial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="value">
                  {formData.type === 'percentage' ? 'Discount Percentage' : 'Trial Days'}
                </Label>
                <Input
                  id="value"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
                  min={1}
                  max={formData.type === 'percentage' ? 100 : 365}
                  required
                  data-testid="input-value"
                />
                <p className="text-xs text-muted-foreground">
                  {formData.type === 'percentage' 
                    ? `${formData.value}% off subscription price` 
                    : `${formData.value} days of free access`}
                </p>
              </div>

              {formData.type === 'trial' && (
                <div className="space-y-2">
                  <Label htmlFor="linkSlug">
                    Trial Link Slug <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">/trial/</span>
                    <Input
                      id="linkSlug"
                      value={formData.linkSlug}
                      onChange={(e) => setFormData({ ...formData, linkSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                      placeholder="back-to-school"
                      data-testid="input-link-slug"
                    />
                  </div>
                  {formData.linkSlug && (
                    <p className="text-xs text-primary font-mono">
                      https://edufocalacademy.com/trial/{formData.linkSlug}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    When someone visits this link, the trial is auto-applied at signup, no code needed.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Applicable Plans</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="plan-monthly"
                      checked={formData.applicablePlans.includes('monthly')}
                      onCheckedChange={() => handlePlanToggle('monthly')}
                      data-testid="checkbox-monthly"
                    />
                    <Label htmlFor="plan-monthly" className="cursor-pointer">Monthly Plan</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="plan-yearly"
                      checked={formData.applicablePlans.includes('yearly')}
                      onCheckedChange={() => handlePlanToggle('yearly')}
                      data-testid="checkbox-yearly"
                    />
                    <Label htmlFor="plan-yearly" className="cursor-pointer">Yearly Plan</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.applicablePlans.length === 0 ? "Applies to all plans" : `Applies to: ${formData.applicablePlans.join(', ')}`}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxUses">Max Uses (Optional)</Label>
                  <Input
                    id="maxUses"
                    type="number"
                    value={formData.maxUses || ""}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Unlimited"
                    min={1}
                    data-testid="input-max-uses"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expiresAt">Expires At (Optional)</Label>
                  <Input
                    id="expiresAt"
                    type="date"
                    value={formData.expiresAt || ""}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value || null })}
                    data-testid="input-expires-at"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  data-testid="switch-active"
                />
                <Label htmlFor="active">Active</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setIsCreateOpen(false);
                  setEditingCode(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) ? "Saving..." : editingCode ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading discount codes...
          </CardContent>
        </Card>
      ) : codes.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No discount codes created yet
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {codes.map((code) => (
            <Card key={code.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {code.linkSlug ? <Link className="w-5 h-5 text-primary" /> : <Tag className="w-5 h-5 text-primary" />}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-mono">{code.code}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        {code.type === 'percentage' ? (
                          <>
                            <Percent className="w-3 h-3" />
                            {code.value}% discount
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3" />
                            {code.value} day trial
                          </>
                        )}
                        {code.applicablePlans && code.applicablePlans.length > 0 && (
                          <span>• {code.applicablePlans.join(', ')}</span>
                        )}
                        {code.linkSlug && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Link className="w-2.5 h-2.5" />
                            Trial Link
                          </Badge>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={code.active ? "default" : "secondary"}>
                      {code.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Usage
                    </div>
                    <div className="font-semibold mt-1">
                      {code.timesUsed}
                      {code.maxUses ? ` / ${code.maxUses}` : ''}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-auto ml-2"
                        onClick={() => setViewingUsage(code.id)}
                        data-testid={`button-view-usage-${code.code}`}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Created
                    </div>
                    <div className="font-semibold mt-1">
                      {format(new Date(code.createdAt), 'MMM d, yyyy')}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Expires
                    </div>
                    <div className="font-semibold mt-1">
                      {code.expiresAt ? format(new Date(code.expiresAt), 'MMM d, yyyy') : 'Never'}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(code)}
                      data-testid={`button-edit-${code.code}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete discount code ${code.code}?`)) {
                          deleteMutation.mutate(code.id);
                        }
                      }}
                      data-testid={`button-delete-${code.code}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {code.linkSlug && (
                  <CopyableLink slug={code.linkSlug} />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!viewingUsage} onOpenChange={(open) => !open && setViewingUsage(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Code Usage History</DialogTitle>
            <DialogDescription>
              Users who have used this discount code
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {usageData.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No usage history yet</p>
            ) : (
              usageData.map((usage) => (
                <Card key={usage.id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">
                          {usage.user.firstName} {usage.user.lastName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {getDisplayName(usage.user)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(usage.usedAt), 'MMM d, yyyy h:mm a')}
                        </div>
                        {usage.discountAmount && (
                          <div className="text-sm font-semibold text-green-600">
                            Saved: JMD ${(usage.discountAmount / 100).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
